<template>
  <div>
    <div v-if="!modelValue.length" class="text-muted small mb-2">Aucun examen demandé.</div>
    <div v-for="(x, i) in modelValue" :key="x.cle" class="ligne mb-2">
      <div class="row g-2 align-items-start">
        <div class="col-md-5">
          <Multiselect v-if="x.modifiable && !lectureSeule" v-model="x.serviceId" :options="services" value-prop="id" label="label"
                       searchable :can-clear="true" placeholder="Examen du catalogue…" @select="choisi(x)" />
          <div v-else class="fw-semibold pt-1">{{ x.type }}</div>
        </div>
        <div class="col-md-4">
          <input v-if="x.modifiable && !lectureSeule" v-model="x.type" class="form-control" placeholder="Nom de l'examen">
          <div v-else class="small text-muted pt-1">{{ x.precision }}</div>
        </div>
        <div class="col-md-3 d-flex align-items-center justify-content-end gap-2">
          <span class="badge" :class="badge(x).classe">{{ badge(x).texte }}</span>
          <span v-if="x.id && PAIEMENTS[x.paiement]" class="badge" :class="PAIEMENTS[x.paiement][1]" :title="x.prix ? fcfa(x.prix) : ''">{{ PAIEMENTS[x.paiement][0] }}</span>
          <span v-if="x.statut === 'VALIDE' && x.vu === false" class="badge bg-primary">Nouveau résultat</span>
          <div v-if="x.modifiable && !lectureSeule" class="form-check form-check-inline m-0" title="Urgent">
            <input :id="'urg' + x.cle" v-model="x.urgent" class="form-check-input" type="checkbox">
            <label class="form-check-label small text-danger" :for="'urg' + x.cle">Urgent</label>
          </div>
          <span v-else-if="x.urgent" class="badge bg-danger">Urgent</span>
          <button v-if="x.modifiable && !lectureSeule" type="button" class="btn btn-sm btn-outline-danger" title="Retirer" @click="retirer(i)"><i class="bi bi-trash"></i></button>
        </div>
      </div>
      <input v-if="x.modifiable && !lectureSeule" v-model="x.precision" class="form-control form-control-sm mt-2" placeholder="Précisions (à jeun, région, renseignements…)">
      <div v-if="!lectureSeule && !x.serviceId && x.type && !x.id" class="small text-warning-emphasis mt-1"><i class="bi bi-exclamation-circle me-1"></i>Hors catalogue : s'il n'existe pas au catalogue sous ce nom exact, il ne sera pas envoyé à la caisse. Choisissez-le dans la liste de gauche si possible.</div>
      <div v-else-if="x.id && !x.serviceId && !x.paiement && x.statut !== 'ANNULE'" class="small text-muted mt-1"><i class="bi bi-info-circle me-1"></i>Examen hors catalogue : non facturé automatiquement.</div>

      <!-- Résultats -->
      <div v-if="(x.resultatsDetailles || []).length" class="mt-2">
        <table class="table table-sm mb-1 resultats">
          <tbody>
            <tr v-for="r in x.resultatsDetailles" :key="r.nom" :class="{ 'table-danger': r.anormal }">
              <td>{{ r.nom }}</td><td class="fw-semibold">{{ r.valeur }} {{ r.unite }}</td><td class="text-muted small">{{ r.normes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="x.resultat && labo" class="small mt-1"><strong>Résultat :</strong> {{ x.resultat }}</div>
      <div v-if="x.conclusion" class="small"><strong>Conclusion :</strong> {{ x.conclusion }}</div>
      <div v-if="x.motifAnnulation" class="small text-danger">Annulé : {{ x.motifAnnulation }}</div>
      <!-- Sans laboratoire dans la clinique : résultat d'un laboratoire extérieur -->
      <textarea v-if="!labo && !lectureSeule" v-model="x.resultat" class="form-control form-control-sm mt-2" rows="2" placeholder="Résultat (laboratoire extérieur)"></textarea>
      <div v-else-if="!labo && x.resultat" class="small mt-1"><strong>Résultat :</strong> {{ x.resultat }}</div>
    </div>
    <button v-if="!lectureSeule" type="button" class="btn btn-sm btn-outline-primary" @click="ajouter"><i class="bi bi-plus-circle me-1"></i>Ajouter un examen</button>
    <div v-if="!lectureSeule" class="small text-muted mt-2"><i class="bi bi-info-circle me-1"></i>Un examen du catalogue (choisi dans la liste, ou saisi sous son nom exact) est envoyé à la caisse (« À payer ») avec son tarif : la caisse n'a rien à ressaisir.</div>
  </div>
</template>

<script>
const STATUTS = {
  PRESCRIT: ['Demandé', 'bg-secondary'],
  PRELEVE: ['Prélevé', 'bg-info text-dark'],
  RESULTAT_SAISI: ['Résultat saisi', 'bg-warning text-dark'],
  VALIDE: ['Résultat validé', 'bg-success'],
  ANNULE: ['Annulé', 'bg-danger'],
}
const PAIEMENTS = {
  A_FACTURER: ['À payer', 'bg-warning text-dark'],
  A_PAYER: ['Facturé, non payé', 'bg-warning text-dark'],
  PAYE: ['Payé', 'bg-success-subtle text-success-emphasis border'],
}
let compteur = 0

export function ligneExamen(x = {}) {
  return {
    cle: ++compteur, id: x.id || null, serviceId: x.serviceId || null, type: x.type || '', precision: x.precision || '',
    urgent: !!x.urgent, statut: x.statut || 'PRESCRIT', modifiable: x.modifiable !== false, resultat: x.resultat || '',
    resultatsDetailles: x.resultatsDetailles || [], conclusion: x.conclusion || '', motifAnnulation: x.motifAnnulation || '',
    paiement: x.paiement || null, prix: x.prix ?? null, vu: x.vu !== false,
  }
}

export default {
  name: 'PrescriptionExamens',
  props: { modelValue: { type: Array, default: () => [] }, services: { type: Array, default: () => [] }, labo: Boolean, lectureSeule: Boolean },
  emits: ['update:modelValue'],
  data() {
    return { PAIEMENTS }
  },
  methods: {
    fcfa(v) {
      return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA'
    },
    badge(x) {
      if (x.id == null) return { texte: 'Nouveau', classe: 'bg-light text-dark border' }
      const [texte, classe] = STATUTS[x.statut] || ['—', 'bg-light text-dark']
      return { texte, classe }
    },
    ajouter() {
      this.$emit('update:modelValue', [...this.modelValue, ligneExamen()])
    },
    retirer(i) {
      this.$emit('update:modelValue', this.modelValue.filter((_, k) => k !== i))
    },
    choisi(x) {
      const s = this.services.find(s => s.id === x.serviceId)
      if (s) x.type = s.nom
    },
  },
}
</script>

<style scoped>
.ligne { border: 1px solid #e3e8ef; border-radius: 8px; padding: 10px; background: #fff; }
.resultats td { padding: 2px 6px; }
</style>
